import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const SIM_DEFS = '/machinesim/api/sim-defs';

function apiErr(e) {
  const fromBody =
    e.response?.data &&
    typeof e.response.data === 'object' &&
    e.response.data.error != null
      ? String(e.response.data.error)
      : null;
  return fromBody || e.message || 'Request failed';
}

export default function StreamMachineTypesCatalogTab() {
  const [typesWithFields, setTypesWithFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const listRes = await axios.get(`${SIM_DEFS}/machine-types`);
        if (cancelled) return;
        const list = Array.isArray(listRes.data) ? listRes.data : [];

        const details = await Promise.all(
          list.map((t) => axios.get(`${SIM_DEFS}/machine-types/${t.id}`)),
        );
        if (cancelled) return;

        setTypesWithFields(details.map((r) => r.data).filter(Boolean));
        setError(null);
      } catch (e) {
        if (!cancelled) setError(apiErr(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <div className="p-8 text-muted-foreground">Loading definitions…</div>;
  }

  if (error) {
    return (
      <div className="p-4 md:p-8 max-w-2xl mx-auto space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Machine type definitions</h2>
        <div
          className="rounded-lg border border-cn-danger/40 bg-cn-danger/10 px-4 py-3 text-sm text-foreground"
          role="alert"
        >
          <p className="font-medium text-destructive">Could not load definitions</p>
          <p className="mt-2">{error}</p>
          <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
            Machine types and stream fields are stored in PostgreSQL. Start Postgres, set{' '}
            <span className="font-mono text-foreground">DATABASE_URL</span> in <span className="font-mono">server/.env</span>,
            run Prisma migrations, and seed (<span className="font-mono">npm run db:*</span> / <span className="font-mono">prisma migrate</span>{' '}
            as documented for this repo). The Machines tab still works without the database.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-2">Machine type definitions</h2>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Stream field definitions describe the fields a machine of this type reports. Timing and
            fault rules are separate; this tab lists stream fields only.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Link
            to="global-core-keys"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-cn-warning/50 text-cn-warning hover:bg-cn-warning/20 text-sm font-medium"
          >
            Global core keys
          </Link>
          <Link
            to="new"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary hover:opacity-90 text-primary-foreground text-sm font-medium"
          >
            New machine type
          </Link>
        </div>
      </div>

      {typesWithFields.map((mt) => (
        <section
          key={mt.id}
          className="rounded-xl border border-border bg-card overflow-hidden"
        >
          <div className="px-4 py-3 bg-secondary border-b border-border flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-mono text-lg text-foreground">
                {mt.code}{' '}
                <span className="text-muted-foreground font-normal text-base">— {mt.name}</span>
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                family: <span className="text-muted-foreground">{mt.family}</span>
                {mt.enabled === false ? ' · disabled' : ''}
                {mt.description ? ` — ${mt.description}` : ''}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to={`${mt.id}/core-fields`}
                className="text-sm text-primary hover:text-primary border border-primary rounded-md px-3 py-1.5 hover:bg-accent"
              >
                Core fields
              </Link>
              <Link
                to={`${mt.id}/edit`}
                className="text-sm text-primary hover:text-primary border border-primary rounded-md px-3 py-1.5 hover:bg-accent"
              >
                Edit type
              </Link>
              <Link
                to={`${mt.id}/hmi-studio`}
                className="text-sm text-violet-300 hover:text-violet-200 border border-violet-800/80 rounded-md px-3 py-1.5 hover:bg-violet-950/50"
              >
                HMI Studio
              </Link>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="p-3 font-medium">core</th>
                  <th className="p-3 font-medium">fieldKey</th>
                  <th className="p-3 font-medium">label</th>
                  <th className="p-3 font-medium">description</th>
                  <th className="p-3 font-medium">dataType</th>
                  <th className="p-3 font-medium">unit</th>
                </tr>
              </thead>
              <tbody className="font-mono text-foreground">
                {(mt.streamFields || []).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-4 text-muted-foreground italic">
                      No stream fields
                    </td>
                  </tr>
                ) : (
                  mt.streamFields.map((f) => (
                    <tr key={f.id || f.fieldKey} className="border-t border-border">
                      <td className="p-3 align-top text-center text-xs">
                        {f.isCore ? <span className="text-cn-warning">yes</span> : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="p-3 align-top">{f.fieldKey}</td>
                      <td className="p-3 align-top text-muted-foreground">{f.label}</td>
                      <td className="p-3 align-top text-muted-foreground text-xs max-w-xs">
                        {f.description ?? '—'}
                      </td>
                      <td className="p-3 align-top">{f.dataType}</td>
                      <td className="p-3 align-top text-muted-foreground">{f.unit ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {typesWithFields.length === 0 && (
        <p className="text-muted-foreground text-sm">No machine types returned from the API.</p>
      )}
    </div>
  );
}
